import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import { CheckCircle2, XCircle, HelpCircle } from 'lucide-react'

/* ─── Sample data used throughout ─── */
const DATA_TABLE = [
  { date: '2024-01-15', offerName: 'CreditCards Plus', offerId: 'offer_101', clicks: 340, conversions: 12 },
  { date: '2024-01-15', offerName: 'InsureMe Now', offerId: 'offer_123', clicks: 210, conversions: 8 },
  { date: '2024-01-15', offerName: 'HomeLoans Direct', offerId: 'offer_102', clicks: 180, conversions: 5 },
  { date: '2024-01-16', offerName: 'CreditCards Plus', offerId: 'offer_101', clicks: 420, conversions: 15 },
  { date: '2024-01-16', offerName: 'InsureMe Now', offerId: 'offer_123', clicks: 195, conversions: 7 },
  { date: '2024-01-16', offerName: 'TravelDeals Pro', offerId: 'offer_145', clicks: 510, conversions: 22 },
  { date: '2024-01-17', offerName: 'InsureMe Now', offerId: 'offer_123', clicks: 275, conversions: 11 },
  { date: '2024-01-17', offerName: 'CreditCards Plus', offerId: 'offer_101', clicks: 390, conversions: 14 },
  { date: '2024-01-17', offerName: 'HomeLoans Direct', offerId: 'offer_102', clicks: 220, conversions: 9 },
  { date: '2024-01-18', offerName: 'InsureMe Now', offerId: 'offer_123', clicks: 310, conversions: 13 },
  { date: '2024-01-18', offerName: 'TravelDeals Pro', offerId: 'offer_145', clicks: 480, conversions: 19 },
  { date: '2024-01-18', offerName: 'CreditCards Plus', offerId: 'offer_101', clicks: 355, conversions: 11 },
]

const OFFER_IDS = [...new Set(DATA_TABLE.map((r) => r.offerId))]

/* ─── Quiz Data ─── */
const QUIZ = [
  {
    question: 'What does SUMIFS do?',
    options: [
      'Counts the number of rows that match one or more conditions',
      'Sums values in a range WHERE one or more conditions are met',
      'Looks up a single value from a table',
      'Filters rows that match a condition into a new range',
    ],
    correctIndex: 1,
    explanation:
      'SUMIFS adds up values in a sum_range, but only for the rows where ALL specified conditions are met. It is the conditional version of SUM.',
  },
  {
    question: 'In SUMIFS, which argument comes FIRST?',
    options: [
      'The criteria_range (where to check conditions)',
      'The criterion (what to match)',
      'The sum_range (the values to add up)',
      'The column header name',
    ],
    correctIndex: 2,
    explanation:
      'Unlike COUNTIF where the range comes first, SUMIFS puts the sum_range first: =SUMIFS(sum_range, criteria_range1, criterion1, ...). This is a common source of confusion.',
  },
  {
    question: 'What does COUNTIFS do?',
    options: [
      'Sums values where conditions are met',
      'Counts rows where one or more conditions are met',
      'Returns the count of all non-empty cells',
      'Counts unique values in a range',
    ],
    correctIndex: 1,
    explanation:
      'COUNTIFS counts the number of rows where ALL specified conditions are met. It does not sum values — it just counts matching rows.',
  },
  {
    question: 'What formula gives you the TOTAL clicks for offer_123 across all dates?',
    context: {
      headers: ['Date', 'Offer ID', 'Clicks'],
      rows: [
        ['2024-01-15', 'offer_123', '340'],
        ['2024-01-15', 'offer_456', '210'],
        ['2024-01-16', 'offer_123', '420'],
        ['2024-01-16', 'offer_456', '180'],
      ],
    },
    options: [
      '=VLOOKUP("offer_123", A:C, 3, FALSE)',
      '=INDEX(C:C, MATCH("offer_123", B:B, 0))',
      '=SUMIFS(C:C, B:B, "offer_123")',
      '=FILTER(C:C, B:B="offer_123")',
    ],
    correctIndex: 2,
    explanation:
      'VLOOKUP and INDEX/MATCH return a value from ONE row (340 or 420, not both). SUMIFS sums the Clicks column for all rows where Offer ID is offer_123: 340 + 420 = 760.',
  },
  {
    question: 'You want the clicks for offer_123 on Jan 15 specifically (one row). Which function?',
    options: [
      'SUMIFS',
      'VLOOKUP or INDEX/MATCH',
      'COUNTIFS',
      'SUM',
    ],
    correctIndex: 1,
    explanation:
      'When you need a value from one specific row, VLOOKUP or INDEX/MATCH is the right tool. SUMIFS would also work here but is designed for aggregating across rows.',
  },
  {
    question: 'How many days did offer_123 have data? Which formula answers this?',
    context: {
      headers: ['Date', 'Offer ID', 'Clicks'],
      rows: [
        ['2024-01-15', 'offer_123', '340'],
        ['2024-01-15', 'offer_456', '210'],
        ['2024-01-16', 'offer_123', '420'],
        ['2024-01-17', 'offer_456', '180'],
        ['2024-01-17', 'offer_123', '275'],
      ],
    },
    options: [
      '=SUMIFS(C:C, B:B, "offer_123")',
      '=VLOOKUP("offer_123", A:C, 1, FALSE)',
      '=COUNTIFS(B:B, "offer_123")',
      '=AVERAGE(C:C)',
    ],
    correctIndex: 2,
    explanation:
      'COUNTIFS counts the number of rows matching your condition. =COUNTIFS(B:B, "offer_123") returns 3 because offer_123 appears on three days.',
  },
  {
    question: 'What does each additional criteria_range + criterion pair in SUMIFS represent?',
    options: [
      'An OR condition — match this OR that',
      'An AND condition — must match this AND that',
      'A NOT condition — exclude this',
      'A separate SUM calculation',
    ],
    correctIndex: 1,
    explanation:
      'Every pair of criteria_range + criterion adds an AND condition. All conditions must be true for a row to be included in the sum.',
  },
  {
    question: 'How do you specify a date range (Jan 15 to Jan 31) in SUMIFS?',
    options: [
      'Use a single criterion with a dash: "2024-01-15 - 2024-01-31"',
      'Use two criteria on the same column: ">="&DATE(2024,1,15) and "<="&DATE(2024,1,31)',
      'Use the DATERANGE function',
      'You cannot filter by date range in SUMIFS',
    ],
    correctIndex: 1,
    explanation:
      'To filter a date range, use the same column twice with two criteria: one for >= the start date and one for <= the end date. Each becomes a separate criteria_range + criterion pair.',
  },
  {
    question: 'What does =SUMIFS(D:D, C:C, "offer_123", A:A, ">="&DATE(2024,1,16), A:A, "<="&DATE(2024,1,17)) return?',
    context: {
      headers: ['A: Date', 'B: Name', 'C: Offer ID', 'D: Clicks'],
      rows: [
        ['2024-01-15', 'InsureMe Now', 'offer_123', '210'],
        ['2024-01-16', 'InsureMe Now', 'offer_123', '195'],
        ['2024-01-16', 'CreditCards Plus', 'offer_101', '420'],
        ['2024-01-17', 'InsureMe Now', 'offer_123', '275'],
        ['2024-01-18', 'InsureMe Now', 'offer_123', '310'],
      ],
    },
    options: [
      '990 (all dates for offer_123)',
      '470 (Jan 16 + Jan 17 for offer_123)',
      '2 (count of matching rows)',
      '195 (first matching row only)',
    ],
    correctIndex: 1,
    explanation:
      'This formula sums column D where column C is "offer_123" AND date >= Jan 16 AND date <= Jan 17. Only rows 2 and 4 match all three conditions: 195 + 275 = 470.',
  },
  {
    question: 'What is the difference between SUMIF (no S) and SUMIFS?',
    options: [
      'They are identical',
      'SUMIF supports only one condition; SUMIFS supports multiple conditions',
      'SUMIF is faster',
      'SUMIFS only works with numbers',
    ],
    correctIndex: 1,
    explanation:
      'SUMIF handles a single condition. SUMIFS handles multiple conditions (and has a different argument order — sum_range comes first). Always use SUMIFS since it works for single and multiple conditions.',
  },
  {
    question: 'You want the average conversion rate for offer_123. Can SUMIFS help?',
    options: [
      'No, SUMIFS only sums — use AVERAGEIFS instead',
      'Yes, divide two SUMIFS: =SUMIFS(conversions, ...) / SUMIFS(clicks, ...)',
      'No, you need to use FILTER + AVERAGE',
      'Yes — SUMIFS, AVERAGEIFS, and FILTER + AVERAGE can all solve this',
    ],
    correctIndex: 3,
    explanation:
      'You could use AVERAGEIFS directly, or calculate it as total conversions / total clicks using two SUMIFS formulas. FILTER + AVERAGE also works. Multiple valid approaches exist.',
  },
  {
    question: 'You need to look up a value for every row in a column of 500 offer IDs. What\'s the best approach?',
    options: [
      'Write 500 separate VLOOKUP formulas',
      'Use one ARRAYFORMULA + INDEX/MATCH to fill the whole column',
      'Use SUMIFS for each row',
      'Copy-paste the data manually',
    ],
    correctIndex: 1,
    explanation:
      'ARRAYFORMULA wraps INDEX/MATCH so a single formula in one cell fills all 500 rows at once. No dragging, no inconsistencies, and it automatically handles new rows.',
  },
  {
    question: 'When would you use QUERY instead of VLOOKUP for a lookup?',
    options: [
      'When you need a simple single-value lookup',
      'When you want to filter, sort, and aggregate in one formula',
      'When the data has fewer than 10 rows',
      'QUERY cannot do lookups',
    ],
    correctIndex: 1,
    explanation:
      'QUERY is overkill for a simple lookup, but it shines when you need to combine filtering, sorting, grouping, or aggregation in a single formula — like a mini database query.',
  },
  {
    question: 'Why would you use SUMIFS instead of SUM + FILTER?',
    options: [
      'SUMIFS is the only way to sum conditionally',
      'SUMIFS is simpler syntax for straightforward conditions and is a more traditional approach',
      'SUM + FILTER does not exist in Google Sheets',
      'SUMIFS is always faster',
    ],
    correctIndex: 1,
    explanation:
      'Both approaches work. SUMIFS is a single function with a well-known pattern, while SUM(FILTER(...)) is more flexible for complex logic. For simple conditions, SUMIFS is more concise and widely understood.',
  },
]

/* ─── Section card wrapper ─── */
function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl border border-slate-200 bg-white p-8"
    >
      {children}
    </motion.div>
  )
}

/* ─── Mini spreadsheet table component ─── */
function DataTableView({ data, highlightRows = [], highlightCol = null, sumResult = null, countResult = null }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-300 shadow-sm">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="border-b border-r border-gray-300 px-2 py-1.5 text-center text-xs text-gray-500 w-8" />
            <th className="border-b border-r border-gray-300 px-2 py-1.5 text-center text-xs text-gray-500 min-w-[80px]">A (Date)</th>
            <th className="border-b border-r border-gray-300 px-2 py-1.5 text-center text-xs text-gray-500 min-w-[110px]">B (Offer Name)</th>
            <th className="border-b border-r border-gray-300 px-2 py-1.5 text-center text-xs text-gray-500 min-w-[80px]">C (Offer ID)</th>
            <th className="border-b border-r border-gray-300 px-2 py-1.5 text-center text-xs text-gray-500 min-w-[60px]">D (Clicks)</th>
            <th className="border-b border-gray-300 px-2 py-1.5 text-center text-xs text-gray-500 min-w-[60px]">E (Conv.)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="bg-gray-100 border-b border-r border-gray-300 text-center text-xs text-gray-500 py-1.5">1</td>
            <td className="border-b border-r border-gray-200 px-2 py-1.5 font-semibold text-gray-700 bg-gray-50">Date</td>
            <td className="border-b border-r border-gray-200 px-2 py-1.5 font-semibold text-gray-700 bg-gray-50">Offer Name</td>
            <td className="border-b border-r border-gray-200 px-2 py-1.5 font-semibold text-gray-700 bg-gray-50">Offer ID</td>
            <td className="border-b border-r border-gray-200 px-2 py-1.5 font-semibold text-gray-700 bg-gray-50">Clicks</td>
            <td className="border-b border-gray-200 px-2 py-1.5 font-semibold text-gray-700 bg-gray-50">Conv.</td>
          </tr>
          {data.map((row, i) => {
            const isHighlighted = highlightRows.includes(i)
            const baseClass = isHighlighted
              ? 'bg-emerald-50'
              : ''
            return (
              <tr key={i} className={baseClass}>
                <td className="bg-gray-100 border-b border-r border-gray-300 text-center text-xs text-gray-500 py-1.5">{i + 2}</td>
                <td className="border-b border-r border-gray-200 px-2 py-1.5 text-gray-700 text-xs">{row.date}</td>
                <td className="border-b border-r border-gray-200 px-2 py-1.5 text-gray-700 text-xs">{row.offerName}</td>
                <td className={`border-b border-r border-gray-200 px-2 py-1.5 font-mono text-xs ${
                  isHighlighted ? 'text-emerald-800 font-semibold' : 'text-gray-700'
                }`}>{row.offerId}</td>
                <td className={`border-b border-r border-gray-200 px-2 py-1.5 text-center ${
                  isHighlighted && highlightCol === 'clicks'
                    ? 'text-emerald-800 font-bold bg-emerald-100'
                    : 'text-gray-700'
                }`}>{row.clicks}</td>
                <td className={`border-b border-gray-200 px-2 py-1.5 text-center ${
                  isHighlighted && highlightCol === 'conversions'
                    ? 'text-emerald-800 font-bold bg-emerald-100'
                    : 'text-gray-700'
                }`}>{row.conversions}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* ─── Section 1: Interactive SUMIFS Demo ─── */
function SumifsDemo() {
  const [selectedOffer, setSelectedOffer] = useState('offer_123')

  const matchingIndices = DATA_TABLE
    .map((row, i) => (row.offerId === selectedOffer ? i : -1))
    .filter((i) => i !== -1)

  const totalClicks = DATA_TABLE
    .filter((row) => row.offerId === selectedOffer)
    .reduce((sum, row) => sum + row.clicks, 0)

  const offerName = DATA_TABLE.find((r) => r.offerId === selectedOffer)?.offerName || ''

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {OFFER_IDS.map((id) => (
          <button
            key={id}
            onClick={() => setSelectedOffer(id)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              selectedOffer === id
                ? 'border-emerald-300 bg-emerald-100 text-emerald-700'
                : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            {id}
          </button>
        ))}
      </div>

      {/* Formula bar */}
      <div className="rounded-lg bg-gray-900 p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-gray-500 font-mono">fx</span>
          <div className="w-px h-3 bg-gray-700" />
        </div>
        <pre className="font-mono text-sm">
          <span className="text-gray-500">=SUMIFS(</span>
          <span className="text-blue-400">D:D</span>
          <span className="text-gray-500">, </span>
          <span className="text-green-400">C:C</span>
          <span className="text-gray-500">, </span>
          <span className="text-amber-400">&quot;{selectedOffer}&quot;</span>
          <span className="text-gray-500">)</span>
        </pre>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={selectedOffer}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          transition={{ duration: 0.2 }}
        >
          <DataTableView
            data={DATA_TABLE}
            highlightRows={matchingIndices}
            highlightCol="clicks"
            sumResult={totalClicks}
          />
        </motion.div>
      </AnimatePresence>

      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
        <p className="text-sm text-emerald-800">
          <strong>{offerName}</strong> ({selectedOffer}) appears in{' '}
          <strong>{matchingIndices.length} rows</strong>. SUMIFS adds up their clicks:{' '}
          <strong>{totalClicks.toLocaleString()}</strong>.
        </p>
      </div>

      {/* Argument breakdown */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <span className="text-xs font-bold text-blue-700">sum_range</span>
          <code className="mt-1 block text-xs font-mono text-blue-800">D:D</code>
          <p className="text-xs text-blue-600 mt-1">The column to add up (Clicks)</p>
        </div>
        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
          <span className="text-xs font-bold text-green-700">criteria_range1</span>
          <code className="mt-1 block text-xs font-mono text-green-800">C:C</code>
          <p className="text-xs text-green-600 mt-1">Where to check the condition (Offer ID column)</p>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <span className="text-xs font-bold text-amber-700">criterion1</span>
          <code className="mt-1 block text-xs font-mono text-amber-800">&quot;{selectedOffer}&quot;</code>
          <p className="text-xs text-amber-600 mt-1">The value to match</p>
        </div>
      </div>
    </div>
  )
}

/* ─── Section 2: COUNTIFS Demo ─── */
function CountifsDemo() {
  const [selectedOffer, setSelectedOffer] = useState('offer_123')
  const [activeMode, setActiveMode] = useState('COUNTIFS')

  const matchingIndices = DATA_TABLE
    .map((row, i) => (row.offerId === selectedOffer ? i : -1))
    .filter((i) => i !== -1)

  const countResult = matchingIndices.length

  const totalClicks = DATA_TABLE
    .filter((row) => row.offerId === selectedOffer)
    .reduce((sum, row) => sum + row.clicks, 0)

  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <div className="flex gap-2">
        {['COUNTIFS', 'SUMIFS'].map((mode) => (
          <button
            key={mode}
            onClick={() => setActiveMode(mode)}
            className={`rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${
              activeMode === mode
                ? mode === 'COUNTIFS'
                  ? 'bg-purple-100 text-purple-700 border-purple-300'
                  : 'bg-blue-100 text-blue-700 border-blue-300'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {mode}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {OFFER_IDS.map((id) => (
          <button
            key={id}
            onClick={() => setSelectedOffer(id)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              selectedOffer === id
                ? 'border-emerald-300 bg-emerald-100 text-emerald-700'
                : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            {id}
          </button>
        ))}
      </div>

      {/* Formula bar */}
      <div className="rounded-lg bg-gray-900 p-4">
        <pre className="font-mono text-sm">
          {activeMode === 'COUNTIFS' ? (
            <>
              <span className="text-gray-500">=COUNTIFS(</span>
              <span className="text-green-400">C:C</span>
              <span className="text-gray-500">, </span>
              <span className="text-amber-400">&quot;{selectedOffer}&quot;</span>
              <span className="text-gray-500">)</span>
            </>
          ) : (
            <>
              <span className="text-gray-500">=SUMIFS(</span>
              <span className="text-blue-400">D:D</span>
              <span className="text-gray-500">, </span>
              <span className="text-green-400">C:C</span>
              <span className="text-gray-500">, </span>
              <span className="text-amber-400">&quot;{selectedOffer}&quot;</span>
              <span className="text-gray-500">)</span>
            </>
          )}
        </pre>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={`${selectedOffer}-${activeMode}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <DataTableView
            data={DATA_TABLE}
            highlightRows={matchingIndices}
            highlightCol={activeMode === 'SUMIFS' ? 'clicks' : null}
            sumResult={activeMode === 'SUMIFS' ? totalClicks : null}
            countResult={activeMode === 'COUNTIFS' ? countResult : null}
          />
        </motion.div>
      </AnimatePresence>

      {/* Comparison callout */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className={`rounded-lg border-2 p-4 transition-all ${
          activeMode === 'COUNTIFS'
            ? 'border-purple-300 bg-purple-50'
            : 'border-gray-200 bg-gray-50'
        }`}>
          <h4 className="font-bold text-gray-800">COUNTIFS</h4>
          <p className="text-sm text-gray-600 mt-1">Counts matching rows</p>
          <code className="mt-2 block text-xs font-mono text-gray-700 bg-white/60 rounded px-2 py-1">
            = {countResult} rows
          </code>
        </div>
        <div className={`rounded-lg border-2 p-4 transition-all ${
          activeMode === 'SUMIFS'
            ? 'border-blue-300 bg-blue-50'
            : 'border-gray-200 bg-gray-50'
        }`}>
          <h4 className="font-bold text-gray-800">SUMIFS</h4>
          <p className="text-sm text-gray-600 mt-1">Sums values from matching rows</p>
          <code className="mt-2 block text-xs font-mono text-gray-700 bg-white/60 rounded px-2 py-1">
            = {totalClicks.toLocaleString()} clicks
          </code>
        </div>
      </div>
    </div>
  )
}

/* ─── Section 3: Decision Framework / Scenario Picker ─── */
const SCENARIOS = [
  {
    question: 'Get me the clicks for offer_123 on Jan 15 (one specific row)',
    correct: 'VLOOKUP / INDEX/MATCH',
    options: ['SUMIFS', 'VLOOKUP / INDEX/MATCH', 'COUNTIFS'],
    explanation: 'You want one value from one specific row. VLOOKUP or INDEX/MATCH are designed for single-row lookups.',
  },
  {
    question: 'Get me total clicks for offer_123 across ALL dates',
    correct: 'SUMIFS',
    options: ['VLOOKUP / INDEX/MATCH', 'SUMIFS', 'COUNTIFS'],
    explanation: 'You need to sum values across multiple matching rows. SUMIFS aggregates — lookup functions only return one row.',
  },
  {
    question: 'How many days did offer_123 have data?',
    correct: 'COUNTIFS',
    options: ['SUMIFS', 'VLOOKUP / INDEX/MATCH', 'COUNTIFS'],
    explanation: 'You need to count matching rows, not sum values. COUNTIFS counts rows where conditions are met.',
  },
  {
    question: 'Get me the conversion rate for offer_123 on Jan 15 (one row, flexible column)',
    correct: 'INDEX/MATCH',
    options: ['INDEX/MATCH', 'SUMIFS', 'COUNTIFS'],
    explanation: 'One specific row, and you might need to grab from any column position. INDEX/MATCH is the most flexible single-row lookup.',
  },
  {
    question: 'Get total conversions for offer_123 between Jan 16 and Jan 18',
    correct: 'SUMIFS with date range',
    options: ['VLOOKUP', 'SUMIFS with date range', 'COUNTIFS'],
    explanation: 'Aggregate values (sum) with multiple conditions including a date range. SUMIFS with >= and <= criteria handles this perfectly.',
  },
  {
    question: 'How many rows have more than 300 clicks?',
    correct: 'COUNTIFS',
    options: ['SUMIFS', 'COUNTIFS', 'VLOOKUP / INDEX/MATCH'],
    explanation: 'You are counting rows that meet a numeric condition. COUNTIFS with the criterion ">300" handles this.',
  },
  {
    question: 'Get total clicks per offer, grouped by offer ID',
    correct: 'QUERY with GROUP BY',
    options: ['SUMIFS', 'QUERY with GROUP BY', 'VLOOKUP / INDEX/MATCH'],
    explanation: 'SUMIFS can sum for one offer at a time, but QUERY with GROUP BY returns all offers\' totals in one formula. When you need grouped aggregation, QUERY is the right tool.',
  },
  {
    question: 'You have a column of 200 offer IDs and need to look up clicks for each one',
    correct: 'ARRAYFORMULA + INDEX/MATCH',
    options: ['SUMIFS', 'ARRAYFORMULA + INDEX/MATCH', 'COUNTIFS'],
    explanation: 'One ARRAYFORMULA + INDEX/MATCH formula fills all 200 rows at once. No dragging, no per-row formulas. This is the bulk lookup approach.',
  },
]

function ScenarioPicker() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selectedAnswer, setSelectedAnswer] = useState(null)
  const [showResult, setShowResult] = useState(false)

  const scenario = SCENARIOS[currentIndex]
  const isCorrect = selectedAnswer === scenario.correct

  const handleSelect = (answer) => {
    setSelectedAnswer(answer)
    setShowResult(true)
  }

  const handleNext = () => {
    setSelectedAnswer(null)
    setShowResult(false)
    setCurrentIndex((prev) => (prev + 1) % SCENARIOS.length)
  }

  return (
    <div className="space-y-4">
      {/* Progress */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">
          Scenario {currentIndex + 1} of {SCENARIOS.length}
        </span>
        <div className="flex gap-1 flex-1">
          {SCENARIOS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                i <= currentIndex ? 'bg-emerald-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
          className="space-y-4"
        >
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-blue-800">{scenario.question}</p>
            </div>
          </div>

          {/* Options */}
          <div className="grid gap-2">
            {scenario.options.map((option) => {
              let btnClass = 'border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700'
              if (showResult) {
                if (option === scenario.correct) {
                  btnClass = 'border-emerald-400 bg-emerald-100 text-emerald-800'
                } else if (option === selectedAnswer && !isCorrect) {
                  btnClass = 'border-red-400 bg-red-100 text-red-800'
                } else {
                  btnClass = 'border-gray-200 bg-gray-50 text-gray-400'
                }
              }
              return (
                <button
                  key={option}
                  onClick={() => !showResult && handleSelect(option)}
                  disabled={showResult}
                  className={`rounded-lg border-2 px-4 py-3 text-sm font-medium text-left transition-all ${btnClass}`}
                >
                  <div className="flex items-center gap-2">
                    {showResult && option === scenario.correct && (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    )}
                    {showResult && option === selectedAnswer && !isCorrect && (
                      <XCircle className="w-4 h-4 text-red-600 shrink-0" />
                    )}
                    {option}
                  </div>
                </button>
              )
            })}
          </div>

          {/* Explanation */}
          {showResult && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-3"
            >
              <div className={`rounded-lg border p-4 ${
                isCorrect
                  ? 'border-emerald-200 bg-emerald-50'
                  : 'border-amber-200 bg-amber-50'
              }`}>
                <p className={`text-sm font-semibold ${isCorrect ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {isCorrect ? 'Correct!' : 'Not quite.'}
                </p>
                <p className={`text-sm mt-1 ${isCorrect ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {scenario.explanation}
                </p>
              </div>
              <button
                onClick={handleNext}
                className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
              >
                {currentIndex < SCENARIOS.length - 1 ? 'Next Scenario' : 'Start Over'}
              </button>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

/* ─── Decision Framework Table ─── */
function DecisionTable() {
  const rows = [
    {
      scenario: 'Get one value from one specific row',
      tool: 'VLOOKUP or INDEX/MATCH',
      why: 'Lookup functions return a single value from one matching row',
      color: 'bg-blue-50 border-blue-200',
      toolColor: 'text-blue-700',
    },
    {
      scenario: 'Sum values across multiple matching rows',
      tool: 'SUMIFS',
      why: 'SUMIFS aggregates (adds up) values where conditions are met',
      color: 'bg-emerald-50 border-emerald-200',
      toolColor: 'text-emerald-700',
    },
    {
      scenario: 'Count how many rows match',
      tool: 'COUNTIFS',
      why: 'COUNTIFS counts matching rows without summing',
      color: 'bg-purple-50 border-purple-200',
      toolColor: 'text-purple-700',
    },
    {
      scenario: 'Look up from any column position',
      tool: 'INDEX/MATCH',
      why: 'Not limited to searching the first column like VLOOKUP',
      color: 'bg-blue-50 border-blue-200',
      toolColor: 'text-blue-700',
    },
    {
      scenario: 'Aggregate with date ranges or multiple conditions',
      tool: 'SUMIFS with multiple criteria',
      why: 'Each criteria_range + criterion pair adds an AND condition',
      color: 'bg-emerald-50 border-emerald-200',
      toolColor: 'text-emerald-700',
    },
  ]

  return (
    <div className="space-y-3">
      {rows.map((row, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -15 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.1, duration: 0.4 }}
          className={`rounded-lg border-2 p-4 ${row.color}`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <p className="text-sm font-medium text-gray-800 flex-1">{row.scenario}</p>
            <span className={`text-sm font-bold ${row.toolColor} whitespace-nowrap`}>
              {row.tool}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">{row.why}</p>
        </motion.div>
      ))}
    </div>
  )
}

/* ─── Section 4: Multiple Criteria Demo ─── */
function MultiCriteriaDemo() {
  const [selectedOffer, setSelectedOffer] = useState('offer_123')
  const [startDate, setStartDate] = useState('2024-01-16')
  const [endDate, setEndDate] = useState('2024-01-17')

  const dates = [...new Set(DATA_TABLE.map((r) => r.date))].sort()

  const matchingIndices = DATA_TABLE
    .map((row, i) =>
      row.offerId === selectedOffer && row.date >= startDate && row.date <= endDate ? i : -1
    )
    .filter((i) => i !== -1)

  const totalClicks = DATA_TABLE
    .filter((row) => row.offerId === selectedOffer && row.date >= startDate && row.date <= endDate)
    .reduce((sum, row) => sum + row.clicks, 0)

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Offer ID</label>
          <select
            value={selectedOffer}
            onChange={(e) => setSelectedOffer(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            {OFFER_IDS.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Start Date (&gt;=)</label>
          <select
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            {dates.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">End Date (&lt;=)</label>
          <select
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            {dates.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Formula bar */}
      <div className="rounded-lg bg-gray-900 p-4 overflow-x-auto">
        <pre className="font-mono text-xs sm:text-sm whitespace-pre-wrap">
          <span className="text-gray-500">=SUMIFS(</span>
          <span className="text-blue-400">D:D</span>
          <span className="text-gray-500">, </span>
          <span className="text-green-400">C:C</span>
          <span className="text-gray-500">, </span>
          <span className="text-amber-400">&quot;{selectedOffer}&quot;</span>
          <span className="text-gray-500">, </span>
          <span className="text-pink-400">A:A</span>
          <span className="text-gray-500">, </span>
          <span className="text-pink-300">&quot;&gt;=&quot;&amp;DATE(2024,1,{parseInt(startDate.split('-')[2], 10)})</span>
          <span className="text-gray-500">, </span>
          <span className="text-cyan-400">A:A</span>
          <span className="text-gray-500">, </span>
          <span className="text-cyan-300">&quot;&lt;=&quot;&amp;DATE(2024,1,{parseInt(endDate.split('-')[2], 10)})</span>
          <span className="text-gray-500">)</span>
        </pre>
      </div>

      <DataTableView
        data={DATA_TABLE}
        highlightRows={matchingIndices}
        highlightCol="clicks"
        sumResult={totalClicks}
      />

      {/* Criteria breakdown */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-700">Three conditions (all AND):</h4>
        <div className="grid gap-2 sm:grid-cols-3">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <span className="text-xs font-bold text-amber-700">Condition 1</span>
            <p className="text-xs text-amber-600 mt-1">Offer ID = &quot;{selectedOffer}&quot;</p>
          </div>
          <div className="rounded-lg border border-pink-200 bg-pink-50 p-3">
            <span className="text-xs font-bold text-pink-700">Condition 2</span>
            <p className="text-xs text-pink-600 mt-1">Date &gt;= {startDate}</p>
          </div>
          <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3">
            <span className="text-xs font-bold text-cyan-700">Condition 3</span>
            <p className="text-xs text-cyan-600 mt-1">Date &lt;= {endDate}</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="rounded-xl border border-blue-200 bg-blue-50 p-4"
      >
        <h4 className="font-bold text-blue-800 text-sm">The pattern</h4>
        <p className="text-sm text-blue-700 mt-1">
          Every pair of <code className="bg-blue-100 rounded px-1">criteria_range, criterion</code>{' '}
          adds another AND condition. You can use the same column twice (like Date here) to create a range.
          The sum_range always comes first, followed by as many condition pairs as you need.
        </p>
      </motion.div>
    </div>
  )
}

/* ─── Main Module ─── */
export default function ModuleSumifs() {
  return (
    <ModuleLayout moduleId={7}>
      <div className="space-y-8">
        {/* Section 1: What is SUMIFS? */}
        <Section delay={0}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">What is SUMIFS?</h2>
              <p className="text-lg text-gray-600">
                SUMIFS adds up values <strong>WHERE conditions are met</strong>. Think of it as SUM
                with built-in filters — it only adds the rows that match your criteria.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-6 text-center"
            >
              <p className="text-emerald-800 text-lg font-semibold">
                SUM + IF conditions = SUMIFS
              </p>
              <p className="text-emerald-600 text-sm mt-1">
                &quot;Add up the clicks, but only where the offer ID is offer_123&quot;
              </p>
            </motion.div>

            <p className="text-gray-700">
              Pick an offer below and watch SUMIFS find all matching rows and sum their clicks:
            </p>

            <SumifsDemo />
          </div>
        </Section>

        {/* Section 2: COUNTIFS */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">COUNTIFS — Counting with Conditions</h2>
              <p className="text-lg text-gray-600">
                Same idea as SUMIFS, but instead of summing values it <strong>counts rows</strong>{' '}
                where conditions are met.
              </p>
            </div>

            <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
              <p className="text-purple-800 text-sm">
                <strong>SUMIFS:</strong> &quot;What is the total number of clicks for offer_123?&quot; → sums the values
              </p>
              <p className="text-purple-800 text-sm mt-2">
                <strong>COUNTIFS:</strong> &quot;How many days did offer_123 have data?&quot; → counts the rows
              </p>
            </div>

            <p className="text-gray-700">
              Toggle between COUNTIFS and SUMIFS to see the difference on the same data:
            </p>

            <CountifsDemo />

            {/* Note on argument order */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-xl border border-amber-200 bg-amber-50 p-6"
            >
              <h4 className="font-bold text-amber-800">Watch the argument order</h4>
              <div className="mt-3 space-y-2">
                <div className="rounded-lg bg-gray-900 p-3">
                  <code className="text-sm text-emerald-400 font-mono">
                    =SUMIFS(<span className="text-blue-400">sum_range</span>, criteria_range, criterion)
                  </code>
                </div>
                <div className="rounded-lg bg-gray-900 p-3">
                  <code className="text-sm text-emerald-400 font-mono">
                    =COUNTIFS(criteria_range, criterion)
                  </code>
                </div>
                <p className="text-sm text-amber-700">
                  SUMIFS puts the <strong>sum_range first</strong> (because it needs to know what to add up).
                  COUNTIFS does not have a sum_range since it just counts rows.
                  This difference in argument order is a common source of mistakes.
                </p>
              </div>
            </motion.div>
          </div>
        </Section>

        {/* Section 3: When to use what */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">
                SUMIFS vs VLOOKUP vs INDEX/MATCH
              </h2>
              <p className="text-lg text-gray-600">
                The key question: do you want <strong>one value from one row</strong>, or do you
                need to <strong>aggregate across many rows</strong>?
              </p>
            </div>

            <DecisionTable />

            <div className="border-t border-gray-200 pt-6">
              <h3 className="mb-4 text-lg font-semibold text-gray-800">
                Test yourself — which function would you use?
              </h3>
              <ScenarioPicker />
            </div>
          </div>
        </Section>

        {/* Section 4: QUERY and ARRAYFORMULA alternatives */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Alternative Approaches: QUERY &amp; ARRAYFORMULA</h2>
              <p className="text-lg text-gray-600">
                QUERY can do lookups too, and ARRAYFORMULA lets you apply a lookup across an entire column at once.
                Here&apos;s how they compare.
              </p>
            </div>

            {/* QUERY as lookup */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-800">QUERY as a lookup</h3>
              <p className="text-sm text-gray-600">
                Instead of VLOOKUP or INDEX/MATCH, you can use QUERY to pull a value from a specific row:
              </p>

              <div className="space-y-2">
                <div className="rounded-lg bg-gray-900 p-4">
                  <p className="text-xs text-gray-500 mb-2">Single value lookup (like VLOOKUP):</p>
                  <pre className="font-mono text-sm whitespace-pre-wrap">
                    <span className="text-gray-500">=QUERY(</span>
                    <span className="text-blue-400">A:E</span>
                    <span className="text-gray-500">, </span>
                    <span className="text-amber-400">&quot;SELECT D WHERE C = &apos;offer_123&apos; AND A = date &apos;2024-01-15&apos;&quot;</span>
                    <span className="text-gray-500">)</span>
                  </pre>
                </div>

                <div className="rounded-lg bg-gray-900 p-4">
                  <p className="text-xs text-gray-500 mb-2">Aggregate (like SUMIFS):</p>
                  <pre className="font-mono text-sm whitespace-pre-wrap">
                    <span className="text-gray-500">=QUERY(</span>
                    <span className="text-blue-400">A:E</span>
                    <span className="text-gray-500">, </span>
                    <span className="text-amber-400">&quot;SELECT SUM(D) WHERE C = &apos;offer_123&apos; LABEL SUM(D) &apos;&apos;&quot;</span>
                    <span className="text-gray-500">)</span>
                  </pre>
                </div>
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="text-sm text-blue-800">
                  <strong>When QUERY shines:</strong> when you want to do more than just look up a value &mdash;
                  e.g., filter + sort + aggregate in one formula, or return multiple columns. It&apos;s like a mini
                  database query.
                </p>
              </div>
            </div>

            {/* ARRAYFORMULA + INDEX/MATCH */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-800">ARRAYFORMULA + INDEX/MATCH</h3>
              <p className="text-sm text-gray-600">
                Need to look up values for an entire column of keys? Instead of dragging a formula down 1000 rows,
                wrap INDEX/MATCH in ARRAYFORMULA:
              </p>

              <div className="rounded-lg bg-gray-900 p-4">
                <p className="text-xs text-gray-500 mb-2">Bulk lookup &mdash; one formula fills the whole column:</p>
                <pre className="font-mono text-sm whitespace-pre-wrap">
                  <span className="text-gray-500">=ARRAYFORMULA(</span>
                  <span className="text-blue-400">INDEX</span>
                  <span className="text-gray-500">(</span>
                  <span className="text-green-400">clicks_col</span>
                  <span className="text-gray-500">, </span>
                  <span className="text-pink-400">MATCH</span>
                  <span className="text-gray-500">(</span>
                  <span className="text-amber-400">lookup_keys</span>
                  <span className="text-gray-500">, </span>
                  <span className="text-green-400">source_keys</span>
                  <span className="text-gray-500">, 0))</span>
                </pre>
              </div>

              <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                <p className="text-sm text-purple-800">
                  <strong>When ARRAYFORMULA shines:</strong> when you have a list of keys and need to look up a
                  value for each one. One formula in one cell fills the entire column &mdash; no dragging, no
                  inconsistencies if new rows are added.
                </p>
              </div>
            </div>

            {/* VLOOKUP with composite key via ARRAYFORMULA */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-800">VLOOKUP on two columns (composite key trick)</h3>
              <p className="text-sm text-gray-600">
                VLOOKUP only searches one column. But what if you need to match on <strong>two columns</strong> —
                like Offer ID <em>and</em> Date? You can concatenate them into a temporary composite key using ARRAYFORMULA:
              </p>

              <div className="rounded-lg bg-gray-900 p-4 space-y-4">
                <div>
                  <p className="text-xs text-gray-500 mb-2">Step 1 — Build a helper column (or use inline):</p>
                  <pre className="font-mono text-sm whitespace-pre-wrap">
                    <span className="text-gray-500">=ARRAYFORMULA(</span>
                    <span className="text-green-400">C2:C</span>
                    <span className="text-amber-400">&amp;</span>
                    <span className="text-pink-400">A2:A</span>
                    <span className="text-gray-500">)</span>
                  </pre>
                  <p className="text-xs text-gray-500 mt-1">This creates values like <code className="text-emerald-400">&quot;offer_1232024-01-15&quot;</code> — a unique key per row.</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-2">Step 2 — VLOOKUP against the composite key:</p>
                  <pre className="font-mono text-sm whitespace-pre-wrap">
                    <span className="text-gray-500">=VLOOKUP(</span>
                    <span className="text-amber-400">&quot;offer_123&quot;&amp;&quot;2024-01-15&quot;</span>
                    <span className="text-gray-500">, {'{'}ARRAYFORMULA(</span>
                    <span className="text-green-400">C2:C</span>
                    <span className="text-amber-400">&amp;</span>
                    <span className="text-pink-400">A2:A</span>
                    <span className="text-gray-500">), </span>
                    <span className="text-blue-400">D2:D</span>
                    <span className="text-gray-500">{'}'}, 2, FALSE)</span>
                  </pre>
                  <p className="text-xs text-gray-500 mt-1">The <code className="text-gray-400">{'{'}...{'}'}</code> builds a virtual 2-column array: composite key + clicks. VLOOKUP searches column 1, returns column 2.</p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <h4 className="font-bold text-emerald-800 text-sm">When to use this</h4>
                  <p className="text-sm text-emerald-700 mt-1">
                    When you need an exact match on two columns (e.g., Offer ID + Date) and want to stick with
                    VLOOKUP instead of switching to INDEX/MATCH.
                  </p>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <h4 className="font-bold text-amber-800 text-sm">Watch out</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    The concatenated key can accidentally match wrong rows if values overlap
                    (e.g., &quot;offer_1&quot; + &quot;23&quot; = &quot;offer_123&quot;). Use a separator
                    like <code className="bg-amber-100 rounded px-1">C2:C&amp;&quot;|&quot;&amp;A2:A</code> to avoid this.
                  </p>
                </div>
              </div>
            </div>

            {/* Comparison table */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-800">The full picture</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">I want to...</th>
                      <th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">Best tool</th>
                      <th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Look up one value from one row', 'VLOOKUP or INDEX/MATCH', 'Simple, purpose-built for single lookups'],
                      ['Look up one value, any column direction', 'INDEX/MATCH', 'Not limited to searching leftmost column'],
                      ['Sum/count across many matching rows', 'SUMIFS / COUNTIFS', 'Purpose-built for conditional aggregation'],
                      ['Look up + filter + sort in one formula', 'QUERY', 'SQL-like power for complex extraction'],
                      ['Aggregate with GROUP BY', 'QUERY', 'SUMIFS can’t group — QUERY can'],
                      ['Look up by two columns (composite key)', 'VLOOKUP + ARRAYFORMULA concat', 'Concatenate columns into one key, then VLOOKUP against it'],
                      ['Apply a lookup to an entire column at once', 'ARRAYFORMULA + INDEX/MATCH', 'One formula fills all rows, stays in sync'],
                      ['Return multiple rows/columns of results', 'FILTER or QUERY', 'Lookup functions only return one value'],
                    ].map(([scenario, tool, why], i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border border-gray-200 px-3 py-2 text-gray-700">{scenario}</td>
                        <td className="border border-gray-200 px-3 py-2 font-semibold text-gray-800">{tool}</td>
                        <td className="border border-gray-200 px-3 py-2 text-gray-500">{why}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-xl border border-amber-200 bg-amber-50 p-4"
            >
              <h4 className="font-bold text-amber-800 text-sm">Rule of thumb</h4>
              <p className="text-sm text-amber-700 mt-1">
                Start with the simplest tool that works. VLOOKUP for quick lookups, SUMIFS for
                conditional totals, QUERY when you need more power. ARRAYFORMULA when you need to
                scale a lookup across many rows without dragging formulas.
              </p>
            </motion.div>
          </div>
        </Section>

        {/* Section 5: Multiple Criteria */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">SUMIFS with Multiple Conditions</h2>
              <p className="text-lg text-gray-600">
                SUMIFS gets really powerful when you add multiple conditions. Each pair of{' '}
                <code className="rounded bg-gray-100 px-2 py-0.5 text-gray-800 font-mono text-sm">
                  criteria_range, criterion
                </code>{' '}
                adds another AND filter.
              </p>
            </div>

            <p className="text-gray-700">
              Try changing the offer and date range to see how SUMIFS narrows down the results:
            </p>

            <MultiCriteriaDemo />
          </div>
        </Section>

        {/* Section 5: Quiz */}
        <Section delay={0.1}>
          <Quiz moduleId={7} questions={QUIZ} />
        </Section>
      </div>
    </ModuleLayout>
  )
}
